"use client";

import { useQuery, useInfiniteQuery, useMutation, useQueries } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import { useGeolocation } from "@/hooks/use-geolocation";
import { useAppEnv } from "@/providers/app-env-provider";
import { storeService, guestStoreService } from "@/services/store.service";
import { reviewService } from "@/services/review.service";
import type { SubmitReviewPayload } from "@/services/review.service";
import type {
  ListStoresParams,
  ReviewParams,
  MenuParams,
  StoreListItem,
  StoreDetail,
  MenuCategory,
  MenuProduct,
} from "@/types";

const COMPANY_ULID = process.env.NEXT_PUBLIC_COMPANY_ULID ?? "";
const DEFAULT_LIMIT = 20;

// ── Store list (paginated) ──

interface UseStoresParams extends Omit<ListStoresParams, "page"> {
  limit?: number;
  enabled?: boolean;
}

export function useStores(params?: UseStoresParams) {
  // Server-seeded auth → stable query key across hydration.
  const { authed } = useAppEnv();
  const { coords } = useGeolocation();

  const limit = params?.limit ?? DEFAULT_LIMIT;

  const geo = authed ? null : { latitude: coords.latitude, longitude: coords.longitude };

  return useInfiniteQuery<StoreListItem[]>({
    queryKey: ["stores", { params, auth: authed, geo }],
    queryFn: ({ pageParam }) => {
      const page = pageParam as number;
      if (authed) {
        return storeService.list({ ...params, page, limit });
      }
      return guestStoreService.list({
        latitude: coords.latitude,
        longitude: coords.longitude,
        company_ulid: COMPANY_ULID,
        ...params,
        page,
        limit,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < limit) return undefined;
      return allPages.length + 1;
    },
    enabled: params?.enabled !== false,
    staleTime: 2 * 60 * 1000,
  });
}

// ── Single store detail ──

export function useStoreDetail(
  ulid: string | null,
  // The guest getById is geo-filtered (400 if the coords are too far from the
  // store). When the store's own coordinates are known (passed from the server),
  // use them so a store outside the visitor's area still loads instead of
  // erroring. Falls back to the visitor's location when not provided.
  geoOverride?: { latitude: number; longitude: number } | null,
  // Server-fetched store, used as React Query initialData so the content is
  // available synchronously on the first (server) render — i.e. the h1/address
  // are in the SSR HTML for SEO instead of a skeleton.
  initialData?: StoreDetail,
) {
  const { authed } = useAppEnv();
  const { coords } = useGeolocation();

  const guestGeo = geoOverride ?? { latitude: coords.latitude, longitude: coords.longitude };
  const geo = authed ? null : guestGeo;

  return useQuery({
    queryKey: ["store-detail", ulid, { auth: authed, geo }],
    queryFn: () => {
      if (authed) {
        return storeService.getById(ulid!);
      }
      return guestStoreService.getById(ulid!, {
        latitude: guestGeo.latitude,
        longitude: guestGeo.longitude,
        company_ulid: COMPANY_ULID,
      });
    },
    enabled: !!ulid,
    staleTime: 2 * 60 * 1000,
    initialData,
  });
}

// ── Store menu categories ──

export function useStoreMenuCategories(storeUlid: string | null) {
  const { authed } = useAppEnv();

  return useQuery({
    queryKey: ["store-menu-categories", storeUlid, { auth: authed }],
    queryFn: () => {
      // `limit` makes the multi-level endpoint nest each category's products in
      // this single response, so flat menus need no per-category fetch.
      const base: MenuParams = { store_ulid: storeUlid!, limit: 100 };
      if (authed) {
        return storeService.getMenu(base) as Promise<MenuCategory[]>;
      }
      return guestStoreService.getMenu({
        ...base,
        company_ulid: COMPANY_ULID,
      }) as Promise<MenuCategory[]>;
    },
    enabled: !!storeUlid,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Store menu products (for a specific category) ──

export function useStoreMenuProducts(
  storeUlid: string | null,
  categoryUlid: string | null,
  options?: { enabled?: boolean }
) {
  const { authed } = useAppEnv();

  return useQuery({
    queryKey: ["store-menu-products", storeUlid, categoryUlid, { auth: authed }],
    queryFn: () => {
      const base: MenuParams = {
        store_ulid: storeUlid!,
        category_ulid: categoryUlid!,
        limit: 100,
      };
      if (authed) {
        return storeService.getMenu(base) as Promise<MenuProduct[]>;
      }
      return guestStoreService.getMenu({
        ...base,
        company_ulid: COMPANY_ULID,
      }) as Promise<MenuProduct[]>;
    },
    enabled: !!storeUlid && !!categoryUlid && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000,
  });
}

// ── Deep recursive product fetch (grocery nested categories) ──
// The multi-level endpoint returns either sub-categories or products for a given
// category_ulid. Grocery stores nest categories (Chat → Croquettes → products),
// so to list a top-level category's products we must walk every descendant.

/** True when a multi-level response holds products (they carry a `price`). */
function isProductLevel(items: unknown[]): items is MenuProduct[] {
  return items.length > 0 && typeof (items[0] as { price?: unknown }).price !== "undefined";
}

async function fetchMenuLevel(storeUlid: string, categoryUlid: string): Promise<unknown[]> {
  const user = useAuthStore.getState().user;
  const base: MenuParams = { store_ulid: storeUlid, category_ulid: categoryUlid, limit: 100 };
  const result = user
    ? await storeService.getMenu(base)
    : await guestStoreService.getMenu({ ...base, company_ulid: COMPANY_ULID });
  return Array.isArray(result) ? result : [];
}

/** Recursively collect every product under a (possibly nested) category. */
export async function fetchCategoryProductsDeep(
  storeUlid: string,
  categoryUlid: string,
  depth = 0,
): Promise<MenuProduct[]> {
  // Safety guard against pathological/cyclic trees.
  if (depth > 4) return [];
  const items = await fetchMenuLevel(storeUlid, categoryUlid);
  if (items.length === 0) return [];
  if (isProductLevel(items)) return items as MenuProduct[];
  const subCats = items as MenuCategory[];
  const nested = await Promise.all(
    subCats.map((sub) => fetchCategoryProductsDeep(storeUlid, sub.ulid, depth + 1)),
  );
  return nested.flat();
}

/** All products of one top-level category, walking nested sub-categories. */
export function useStoreCategoryProductsDeep(storeUlid: string | null, categoryUlid: string | null) {
  const user = useAuthStore((s) => s.user);
  return useQuery<MenuProduct[]>({
    queryKey: ["store-category-products-deep", storeUlid, categoryUlid, { auth: !!user }],
    queryFn: () => fetchCategoryProductsDeep(storeUlid!, categoryUlid!),
    enabled: !!storeUlid && !!categoryUlid,
    staleTime: 2 * 60 * 1000,
  });
}

// ── All products across all categories (for filter/sort views) ──

export function useAllStoreProducts(storeUlid: string, categories: MenuCategory[]) {
  const { authed } = useAppEnv();

  // Categories from the multi-level call already carry their products; only the
  // ones missing them (e.g. nested grocery categories) need a per-category fetch.
  const nested = categories.flatMap((cat) => cat.products ?? []);
  const toFetch = categories.filter((cat) => !cat.products);

  const results = useQueries({
    queries: toFetch.map((cat) => ({
      queryKey: ["store-menu-products", storeUlid, cat.ulid, { auth: authed }],
      queryFn: () => {
        const base: MenuParams = { store_ulid: storeUlid, category_ulid: cat.ulid, limit: 100 };
        if (authed) {
          return storeService.getMenu(base) as Promise<MenuProduct[]>;
        }
        return guestStoreService.getMenu({ ...base, company_ulid: COMPANY_ULID }) as Promise<MenuProduct[]>;
      },
      staleTime: 2 * 60 * 1000,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const fetched = results.flatMap((r) => (r.data as MenuProduct[] | undefined) ?? []);
  const products = [...nested, ...fetched];
  return { products, isLoading };
}

// ── Product addons ──

type MenuAddonT = import("@/types").MenuAddon;

export const productAddonsKey = (storeUlid: string | null, productUlid: string | null, auth: boolean) =>
  ["product-addons", storeUlid, productUlid, { auth }] as const;

/** Standalone fetcher (also usable via queryClient.fetchQuery for lazy lookups). */
export async function fetchProductAddons(storeUlid: string, productUlid: string): Promise<MenuAddonT[]> {
  const user = useAuthStore.getState().user;
  const base: MenuParams = { store_ulid: storeUlid, product_ulid: productUlid, limit: 100 };
  const result = user
    ? await storeService.getMenu(base)
    : await guestStoreService.getMenu({ ...base, company_ulid: COMPANY_ULID });
  // When product_ulid is set the API returns the product object (not an array);
  // extract the embedded addons array from it.
  const product = result as unknown as { addons?: MenuAddonT[] };
  if (product && !Array.isArray(product) && Array.isArray(product.addons)) {
    return product.addons;
  }
  return Array.isArray(result) ? (result as unknown as MenuAddonT[]) : [];
}

/** True when a product has at least one addon group that requires a selection. */
export function addonsRequireSelection(addons: MenuAddonT[] | undefined | null): boolean {
  return (addons ?? []).some((a) => a.minimum_selection_required === true);
}

export function useProductAddons(
  storeUlid: string | null,
  productUlid: string | null,
  options?: { enabled?: boolean }
) {
  const user = useAuthStore((s) => s.user);

  return useQuery<MenuAddonT[]>({
    queryKey: productAddonsKey(storeUlid, productUlid, !!user),
    queryFn: () => fetchProductAddons(storeUlid!, productUlid!),
    enabled: !!storeUlid && !!productUlid && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000,
  });
}

// ── Store config ──

export function useStoreConfig(ulid: string | null) {
  const { authed } = useAppEnv();

  return useQuery({
    queryKey: ["store-config", ulid, { auth: authed }],
    queryFn: () => {
      if (authed) {
        return storeService.getConfig(ulid!);
      }
      return guestStoreService.getConfig(ulid!, COMPANY_ULID);
    },
    enabled: !!ulid,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Store reviews ──

export function useStoreReviews(ulid: string | null, params?: ReviewParams) {
  const { authed } = useAppEnv();

  return useQuery({
    queryKey: ["store-reviews", ulid, params, { auth: authed }],
    queryFn: () => {
      if (authed) {
        return storeService.getReviews(ulid!, params);
      }
      return guestStoreService.getReviews(ulid!, {
        ...params,
        company_ulid: COMPANY_ULID,
      });
    },
    enabled: !!ulid,
    staleTime: 2 * 60 * 1000,
  });
}

// ── Store menu search (no category, search across whole menu) ──

export function useStoreMenuSearch(storeUlid: string | null, search: string) {
  const user = useAuthStore((s) => s.user);
  const COMPANY_ULID = process.env.NEXT_PUBLIC_COMPANY_ULID ?? "";

  return useQuery({
    queryKey: ["store-menu-search", storeUlid, search, { auth: !!user }],
    queryFn: () => {
      const base: MenuParams = { store_ulid: storeUlid!, search, limit: 50 };
      if (user) {
        return storeService.getMenu(base) as Promise<MenuProduct[]>;
      }
      return guestStoreService.getMenu({ ...base, company_ulid: COMPANY_ULID }) as Promise<MenuProduct[]>;
    },
    enabled: !!storeUlid && search.trim().length > 0,
    staleTime: 30 * 1000,
  });
}

// ── Store reviews (paginated/infinite) ──

export function useInfiniteStoreReviews(ulid: string | null, params?: Omit<ReviewParams, "page">) {
  const user = useAuthStore((s) => s.user);
  const COMPANY_ULID = process.env.NEXT_PUBLIC_COMPANY_ULID ?? "";

  return useInfiniteQuery({
    queryKey: ["store-reviews-infinite", ulid, params, { auth: !!user }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const p = { ...params, page: pageParam as number, limit: 10 };
      if (user) {
        return storeService.getReviews(ulid!, p);
      }
      return guestStoreService.getReviews(ulid!, { ...p, company_ulid: COMPANY_ULID });
    },
    getNextPageParam: (last) => last.meta.hasMore ? last.meta.page + 1 : undefined,
    enabled: !!ulid,
    staleTime: 2 * 60 * 1000,
  });
}

// ── App categories ──

export function useAppStoreCategories() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ["store-app-categories", { auth: !!user }],
    queryFn: () => storeService.getAppCategories(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Submit review ──

export function useSubmitReview() {
  return useMutation({
    mutationFn: (payload: SubmitReviewPayload) => reviewService.submit(payload),
    onError: () => {
      // noop
    },
  });
}
